import { useWindowResize } from '@toggle/helpers';
import { TransformedXAxisType } from '@toggle/toggle';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useMemo, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  Y_AXIS_LABEL_WIDTH,
} from '../apache-config';
import { tooltipFormatter } from '../utils';
import { DATA_ZOOM_OPTIONS, LENGTH_TO_SHOW_DATA_ZOOM } from './utils';

interface SingleSeries {
  name: string;
  data: (number | null)[];
  yAxisIndex?: number;
}

interface SingleYAxis {
  name: string;
  formatter?: string;
}

export interface BasicLineChartProps extends ApacheChartBase {
  chartType: 'apache-line';
  xAxis: TransformedXAxisType;
  yAxis?: SingleYAxis[];
  series: SingleSeries[];
  barData?: string[];
}

export const BasicLineChart = ({
  theme = 'dark_default',
  chartType,
  series,
  xAxis,
  yAxis,
  barData,
  onInit,
  isModal,
}: BasicLineChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  const formattedYAxis = useMemo<
    echarts.YAXisComponentOption | echarts.YAXisComponentOption[]
  >(() => {
    if (yAxis?.length) {
      return yAxis.map((item, index) => ({
        type: 'value',
        name: item.name,
        position: index > 0 ? 'right' : 'left',
        alignTicks: true,
        offset: index > 1 ? (Y_AXIS_LABEL_WIDTH / 2) * index : 0,
        ...(item.formatter && {
          axisLabel: {
            formatter: `{value} ${item.formatter}`,
          },
        }),
      }));
    }

    if (barData) {
      const lineData = series.filter(item => !barData.includes(item.name));
      return [
        {
          type: 'value',
          name: barData[0],
        },
        {
          type: 'value',
          name: lineData?.[0]?.name || '',
          boundaryGap: ['5%', '5%'],
        },
      ];
    }

    return {
      type: 'value',
      boundaryGap: ['5%', '5%'],
    };
  }, [chartType]);

  const isShowDataZoom = xAxis.length >= LENGTH_TO_SHOW_DATA_ZOOM;
  const customGridForMultipleYAxis =
    yAxis?.length && yAxis?.length - 1 > 1
      ? {
          right: '15%',
        }
      : undefined;

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const option = createApacheConfig({
      theme,
      isModal,
      options: {
        animation: false,
        yAxis: formattedYAxis,
        xAxis: {
          data: xAxis,
        },
        series: series.map(i => {
          const isBar = barData?.includes(i.name);
          const isMultipleYAxis = !!yAxis?.length;

          return {
            ...i,
            type: isBar ? 'bar' : 'line',
            ...(!isBar &&
              isMultipleYAxis && {
                yAxisIndex: i.yAxisIndex,
              }),
          };
        }),
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none',
          },
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: true,
              isShowMarker: true,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        ...(customGridForMultipleYAxis && { grid: customGridForMultipleYAxis }),
        ...(isShowDataZoom && { ...DATA_ZOOM_OPTIONS }),
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [isModal, chartType, barData, series, xAxis, yAxis, width, height]);

  return <div ref={ref} />;
};
